import {
  Component,
  OnInit,
  inject,
  signal,
  computed,
  ViewChild,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToolbarModule } from 'primeng/toolbar';
import { TagModule } from 'primeng/tag';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';

import { MessageService, ConfirmationService } from 'primeng/api';
import { LoadingOverlayComponent } from '../shared/loading-overlay.component';
import {
  Persona,
  PersonaFiltro,
  createEmptyPersona,
  createEmptyFiltro,
  getNombreCompleto,
  isActivo,
  TIPO_NIT
} from 'src/app/domain/model/persona/persona';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import { PERSONA_MAX_LENGTHS } from 'src/app/common/persona-utils';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS,
  TIPOS_SEXO
} from 'src/app/common/constants-ejb';
import {
  onlyLettersKeypress,
  onlyNumbersKeypress,
  sanitizeLettersOnly,
  sanitizeIdentificacion
} from 'src/app/common/input-utils';

import { PersonaDialogComponent } from './persona-dialog.component';
import { environment } from 'src/environments/environment';
import { personasListadoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

import { ActividadEconomicaRepository } from 'src/app/domain/service/actividad-economica/actividad-economica-repository';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';

/**
 * Componente de administración de personas
 * POC: Migración de AdminPersonasView del sistema legacy GWT
 */
@Component({
  selector: 'app-personas-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    CardModule,
    FieldsetModule,
    InputTextModule,
    InputNumberModule,
    DialogModule,
    DropdownModule,
    ToastModule,
    ConfirmDialogModule,
    ToolbarModule,
    TagModule,
    RadioButtonModule,
    TooltipModule,
    PersonaDialogComponent,
    LoadingOverlayComponent,
    BusquedaPaginadorComponent
  ],
  providers: [MessageService, ConfirmationService],
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  template: `
    <div class="p-6 max-w-7xl mx-auto">
      <!-- Toast y Confirm Dialog -->
      <p-toast></p-toast>
      <p-confirmDialog></p-confirmDialog>

      <!-- Overlay de carga global -->
      <app-loading-overlay [blocked]="loading()" />

      <!-- Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-800 mb-1">
          Administración Personas
        </h1>
      </div>

      <!-- Toolbar con botón Nuevo -->
      <div class="mb-4 flex justify-end">
        <button
          pButton
          label="Nuevo"
          icon="pi pi-plus"
          class="p-button"
          (click)="openNew()"
        ></button>
      </div>

      <!-- Filtros de Consulta -->
      <p-fieldset
        legend="Filtros Consulta de Personas"
        [toggleable]="true"
        styleClass="mb-4"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <!-- Tipo e Identificación -->
          <div class="flex gap-2 col-span-1 md:col-span-2 lg:col-span-1">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Identificación</label
              >
              <p-dropdown
                [(ngModel)]="filtro.tipoId"
                [options]="tiposIdentificacion()"
                optionLabel="descripcion"
                optionValue="codigo"
                placeholder="Tipo..."
                [showClear]="true"
                [filter]="true"
                filterBy="descripcion"
                appendTo="body"
                (onChange)="onFiltroTipoChange()"
                [style]="{ width: '100%' }"
              >
              </p-dropdown>
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >&nbsp;</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.numId"
                placeholder="Número..."
                [maxlength]="maxLengths.numId"
                (keypress)="onIdentificacionKeypress($event)"
                class="w-full"
              />
            </div>
          </div>

          <!-- Campos para Persona Jurídica (NIT) -->
          @if (isFiltroPersonaJuridica()) {
            <div class="col-span-1 md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Nombre Comercial</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.nombreComercial"
                placeholder="Nombre comercial..."
                [maxlength]="maxLengths.dsnombre_comercial"
                class="w-full"
              />
            </div>
          }

          <!-- Campos para Persona Natural (CC, CE, etc.) -->
          @if (!isFiltroPersonaJuridica()) {
            <!-- Primer Nombre -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Primer Nombre</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.nombre1"
                placeholder="Primer nombre..."
                [maxlength]="maxLengths.nombre1"
                class="w-full"
              />
            </div>

            <!-- Segundo Nombre -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Segundo Nombre</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.nombre2"
                placeholder="Segundo nombre..."
                [maxlength]="maxLengths.nombre2"
                class="w-full"
              />
            </div>

            <!-- Primer Apellido -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Primer Apellido</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.apellido1"
                placeholder="Primer apellido..."
                [maxlength]="maxLengths.apellido1"
                class="w-full"
              />
            </div>

            <!-- Segundo Apellido -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1"
                >Segundo Apellido</label
              >
              <input
                pInputText
                [(ngModel)]="filtro.apellido2"
                placeholder="Segundo apellido..."
                [maxlength]="maxLengths.apellido2"
                class="w-full"
              />
            </div>
          }

          <!-- Estado Activo (Radio Buttons) -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Activo</label
            >
            <div class="flex gap-4">
              <div class="flex items-center">
                <p-radioButton
                  name="activo"
                  value="S"
                  [(ngModel)]="filtro.activo"
                  inputId="activoSi"
                >
                </p-radioButton>
                <label for="activoSi" class="ml-2 text-sm">Sí</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="activo"
                  value="N"
                  [(ngModel)]="filtro.activo"
                  inputId="activoNo"
                >
                </p-radioButton>
                <label for="activoNo" class="ml-2 text-sm">No</label>
              </div>
              <div class="flex items-center">
                <p-radioButton
                  name="activo"
                  value=""
                  [(ngModel)]="filtro.activo"
                  inputId="activoTodos"
                >
                </p-radioButton>
                <label for="activoTodos" class="ml-2 text-sm">Todos</label>
              </div>
            </div>
          </div>

          <!-- Botones Buscar y Limpiar -->
          <div
            class="flex flex-wrap items-end justify-end sm:justify-start gap-3 col-span-1 md:col-span-2 lg:col-span-3"
          >
            <button
              pButton
              label="Limpiar"
              icon="pi pi-eraser"
              class="p-button-secondary p-button-outlined"
              (click)="limpiarFiltros()"
            ></button>
            <button
              pButton
              label="Buscar"
              icon="pi pi-search"
              class="p-button-primary"
              [loading]="loading()"
              (click)="buscar()"
            ></button>
          </div>
        </div>
      </p-fieldset>

      <!-- Listado de Personas (solo visible después de buscar) -->
      <p-card *ngIf="busquedaRealizada()" class="mb-4">
        <ng-template pTemplate="header">
          <div class="p-3 pb-0 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-700 m-0">
              Listado de Personas
            </h3>
            <span class="text-sm text-gray-500" *ngIf="totalElements() > 0">
              Registros del {{ registroInicio() }} hasta el
              {{ registroFin() }} de {{ totalElements() | number }}
            </span>
          </div>
        </ng-template>

        <div class="md:hidden space-y-3 p-3">
          @if (personas().length === 0) {
            <div class="text-center p-6 text-gray-500">
              No se encontraron personas
            </div>
          }
          @for (persona of personas(); track persona.nmidentificacion) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Nombre
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                >
                  {{ getNombreCompleto(persona) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400"
                    >Tipo identificación:</span
                  >
                  <span class="text-gray-800 dark:text-gray-200">{{
                    getTipoIdLabel(persona.cdtipo_identificacion)
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Número:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    persona.nmidentificacion
                  }}</span>
                </div>
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Activo:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    isActivo(persona) ? 'Sí' : 'No'
                  }}</span>
                </div>
              </div>
              <button
                pButton
                icon="pi pi-pencil"
                label="Modificar"
                class="p-button-outlined p-button-sm min-h-[44px] w-full"
                [disabled]="loading() || openingPersonaDialog()"
                (click)="editPersona(persona)"
              ></button>
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="personas()"
            [paginator]="false"
            [rows]="pageSize()"
            [loading]="loading()"
            [scrollable]="true"
            responsiveLayout="scroll"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || '72rem'
            }"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '400px'"
            [styleClass]="tableStyleClass()"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Tipo Identificación"
                    >Tipo Identificación</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Número Identificación"
                    >Número Identificación</span
                  >
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Nombre">Nombre</span>
                </th>
                <th pResizableColumn style="width: 80px">
                  <span class="grid-header-text" title="Activo">Activo</span>
                </th>
                <th pResizableColumn style="width: 100px">
                  <span class="grid-header-text" title="Modificar"
                    >Modificar</span
                  >
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-persona>
              <tr>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="
                      textoTabla(getTipoIdLabel(persona.cdtipo_identificacion))
                    "
                    >{{
                      textoTabla(getTipoIdLabel(persona.cdtipo_identificacion))
                    }}</span
                  >
                </td>
                <td class="font-mono">
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(persona.nmidentificacion)"
                    >{{ textoTabla(persona.nmidentificacion) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="textoTabla(getNombreCompleto(persona))"
                    >{{ textoTabla(getNombreCompleto(persona)) }}</span
                  >
                </td>
                <td class="text-center">
                  <span
                    class="grid-cell-text"
                    [attr.title]="isActivo(persona) ? 'Sí' : 'No'"
                    >{{ isActivo(persona) ? 'Sí' : 'No' }}</span
                  >
                </td>
                <td class="text-center">
                  <button
                    pButton
                    icon="pi pi-pencil"
                    class="p-button-rounded p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                    [disabled]="loading() || openingPersonaDialog()"
                    (click)="editPersona(persona)"
                    pTooltip="Modificar"
                  ></button>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5" class="text-center p-6 text-gray-500">
                  <i class="pi pi-inbox text-4xl mb-3 block"></i>
                  No se encontraron personas
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Paginación -->
        <div class="mt-4 px-4 pb-4">
          <app-busqueda-paginador
            [currentPage]="currentPage()"
            [totalPages]="totalPages()"
            [hasPrevious]="hasPrevious()"
            [hasNext]="hasNext()"
            [pageSizeOptions]="pageSizeOptions"
            [selectedPageSize]="selectedPageSize"
            (pageSizeChange)="selectedPageSize = $event; onPageSizeChange()"
            (first)="irPrimeraPagina()"
            (previous)="loadPreviousPage()"
            (next)="loadNextPage()"
            (last)="irUltimaPagina()"
          ></app-busqueda-paginador>
        </div>
      </p-card>

      <!-- Dialog Crear/Editar Persona -->
      <app-persona-dialog
        [(visible)]="dialogVisible"
        [editMode]="editMode()"
        [persona]="personaForm"
        [tiposEmpresa]="tiposEmpresa()"
        [tamaniosEmpresa]="tamaniosEmpresa()"
        (onSave)="onDialogSave($event)"
        (onCancel)="onDialogCancel()"
      >
      </app-persona-dialog>
    </div>
  `
})
export class PersonasListComponent implements OnInit {
  // Inyectando PUERTOS (no adaptadores) - Arquitectura Hexagonal
  private readonly personaRepository = inject(PersonaRepository);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);

  // ViewChild para el diálogo de persona
  @ViewChild(PersonaDialogComponent) personaDialog!: PersonaDialogComponent;

  // Estado reactivo
  personas = signal<Persona[]>([]);
  loading = signal(false);
  saving = signal(false);
  editMode = signal(false);
  openingPersonaDialog = signal(false);
  dialogVisible = false;
  activeTabIndex = signal(0); // Tab activo en el diálogo (0: Datos, 1: Direcciones, 2: Contactos)
  busquedaRealizada = signal(false); // Controla visibilidad de la tabla
  submitted = signal(false); // Indica si se intentó guardar (para mostrar errores)

  // Filtros de búsqueda
  filtro: PersonaFiltro = createEmptyFiltro();

  // Estado de paginación con cursor
  nextCursor = signal<string | null>(null);
  previousCursor = signal<string | null>(null);
  // Flags de navegación: vienen de la respuesta del servidor en cada carga
  // exitosa (skill frontend-girop §17.7), no se deducen del número de página.
  hasNext = signal(false);
  hasPrevious = signal(false);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  currentPage = signal(1);
  totalElements = signal(0);
  cursorHistory: string[] = []; // Para navegación hacia atrás

  readonly tableConfig = computed(() =>
    personasListadoPreset.createConfig({
      data: this.personas(),
      style: {
        tableClassName: 'tabla-cambio-orientacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron personas'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: '72rem',
        scrollHeight: '400px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de listado de personas',
        announceSelection: false,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  // Propiedades computadas para paginación
  totalPages = computed(() => {
    const total = this.totalElements();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  registroInicio = computed(() => {
    if (this.totalElements() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  registroFin = computed(() => {
    if (this.totalElements() === 0) return 0;
    const fin = this.currentPage() * this.pageSize();
    return Math.min(fin, this.totalElements());
  });

  // Indica si se puede ir a la última página (hay páginas restantes)
  canGoToLastPage = computed(() => {
    return this.currentPage() < this.totalPages();
  });

  // Opciones de tamaño de página (legacy: ConstantsEJB)
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  // Formulario
  personaForm: Persona = createEmptyPersona();

  private readonly actividadEconomicaRepository = inject(
    ActividadEconomicaRepository
  );
  private readonly catalogoRepository = inject(CatalogoRepository);

  // Opciones de dropdowns (cargados desde BD)
  tiposIdentificacion = signal<CatalogoOption[]>([]);
  tiposSexo = TIPOS_SEXO;
  tiposEmpresa = signal<CatalogoOption[]>([]);
  tamaniosEmpresa = signal<CatalogoOption[]>([]);

  // Constantes de validación
  maxLengths = PERSONA_MAX_LENGTHS;

  // Handlers de validación para keypress
  onNameKeypress = onlyLettersKeypress;
  /**
   * Número de identificación del filtro: solo dígitos.
   * Legacy: AdminPersonasViewImpl L348 — tbNumIdPersona.setRegex(REGEX_JUST_NUMBERS).
   */
  onIdentificacionKeypress = onlyNumbersKeypress;

  // Handlers de sanitización para input
  sanitizeName = sanitizeLettersOnly;
  sanitizeId = sanitizeIdentificacion;

  estadosOptions = [
    { label: 'Activo', value: 'S' },
    { label: 'Inactivo', value: 'N' }
  ];

  // Fecha máxima de nacimiento (hoy)
  maxFechaNacimiento = new Date();

  // Helpers
  getNombreCompleto = getNombreCompleto;
  isActivo = isActivo;

  /**
  * Indica si el tipo de identificación seleccionado es NIT (persona jurídica)
   * Condiciona la visibilidad de campos en el formulario del MODAL
   */
  isPersonaJuridica(): boolean {
    return this.personaForm.cdtipo_identificacion === TIPO_NIT;
  }

  /**
  * Indica si el tipo de identificación del FILTRO de búsqueda es NIT
   * Condiciona la visibilidad de campos en el formulario de búsqueda
   */
  isFiltroPersonaJuridica(): boolean {
    return this.filtro.tipoId === TIPO_NIT;
  }

  /**
  * Limpia los campos de nombre cuando cambia el tipo de identificación en el filtro
   * Si es NIT: limpia nombre1, nombre2, apellido1, apellido2
   * Si no es NIT: limpia nombreComercial
   */
  onFiltroTipoChange(): void {
    if (this.isFiltroPersonaJuridica()) {
      // Limpiar campos de persona natural
      this.filtro.nombre1 = '';
      this.filtro.nombre2 = '';
      this.filtro.apellido1 = '';
      this.filtro.apellido2 = '';
    } else {
      // Limpiar campo de persona jurídica
      this.filtro.nombreComercial = '';
    }
  }

  ngOnInit(): void {
    // Cargar catálogos desde BD
    this.cargarCatalogos();
  }

  /**
   * Carga los catálogos de tipos y tamaños de empresa desde la BD
   */
  private async cargarCatalogos(): Promise<void> {
    try {
      const [tiposId, tipos, tamanios] = await Promise.all([
        this.catalogoRepository.getTiposIdentificacion(),
        this.catalogoRepository.getTiposEmpresa(),
        this.catalogoRepository.getTamaniosEmpresa()
      ]);
      this.tiposIdentificacion.set(tiposId);
      this.tiposEmpresa.set(tipos);
      this.tamaniosEmpresa.set(tamanios);
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando catálogos:', error);
      }
    }
  }

  /**
   * Ejecuta la búsqueda con los filtros actuales
   */
  async buscar(): Promise<void> {
    this.cursorHistory = [];
    // Obtener conteo total primero
    await this.cargarConteoTotal();
    // Búsqueda nueva: siempre página 1 (currentPage cambia solo si hay respuesta)
    await this.loadPersonas(undefined, 1);
    // Mostrar la tabla de resultados
    this.busquedaRealizada.set(true);
  }

  /**
   * Obtiene el conteo total de registros con los filtros actuales
   */
  async cargarConteoTotal(): Promise<void> {
    try {
      const total = await this.personaRepository.countWithFilters(this.filtro);
      this.totalElements.set(total);
    } catch (error) {
      if (!environment.production) {
        console.error('Error obteniendo conteo:', error);
      }
      // Sin total: se oculta el "Registros ... de N". La navegación no depende
      // del total, usa los flags hasNext/hasPrevious de la respuesta.
      this.totalElements.set(0);
    }
  }

  /**
   * Limpia los filtros y oculta la tabla
   */
  limpiarFiltros(): void {
    this.filtro = createEmptyFiltro();
    // Limpiar resultados y ocultar tabla
    this.personas.set([]);
    this.totalElements.set(0);
    this.currentPage.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursor.set(null);
    this.previousCursor.set(null);
    this.cursorHistory = [];
    this.busquedaRealizada.set(false);
  }

  /**
   * Aplica una respuesta exitosa: lista, cursores opacos, flags de navegación y
   * número de página destino. Solo se invoca con respuesta exitosa, así que un
   * fallo deja intacto el estado anterior.
   */
  private aplicarRespuesta(
    response: {
      content: Persona[];
      nextCursor: string | null;
      previousCursor: string | null;
      hasNext: boolean;
      hasPrevious: boolean;
    },
    pagina: number
  ): void {
    this.personas.set(response.content);
    this.nextCursor.set(response.nextCursor);
    this.previousCursor.set(response.previousCursor);
    // NO sobrescribir totalElements aquí - se obtiene del endpoint /count
    this.hasNext.set(!!response.hasNext);
    this.hasPrevious.set(!!response.hasPrevious);
    this.currentPage.set(pagina);
  }

  /**
   * Carga la lista de personas desde el backend con paginación y filtros.
   * @param cursor cursor opaco (undefined = primera página)
   * @param pagina página destino; currentPage cambia solo tras la respuesta
   */
  async loadPersonas(cursor?: string, pagina = 1): Promise<void> {
    this.loading.set(true);
    try {
      const response = await this.personaRepository.getPageWithFilters(
        cursor,
        this.pageSize(),
        this.filtro
      );
      this.aplicarRespuesta(response, pagina);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las personas'
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Va a la primera página
   */
  async irPrimeraPagina(): Promise<void> {
    if (this.loading()) return;
    this.cursorHistory = [];
    await this.loadPersonas(undefined, 1);
  }

  /**
   * Va a la última página
  * Usa el endpoint /ultima para obtener los últimos registros directamente
   */
  async irUltimaPagina(): Promise<void> {
    if (this.loading() || !this.canGoToLastPage()) {
      return;
    }

    // currentPage = ceil(totalElements / size), se fija tras la respuesta
    const paginaUltima = this.totalPages();
    this.loading.set(true);
    try {
      const response = await this.personaRepository.getLastPage(
        this.pageSize(),
        this.filtro
      );
      this.aplicarRespuesta(response, paginaUltima);
      // Limpiar historial de navegación ya que estamos en la última página
      this.cursorHistory = [];
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar la última página'
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Carga la siguiente página usando nextCursor del backend
   */
  async loadNextPage(): Promise<void> {
    if (this.loading() || !this.hasNext()) return;
    const cursor = this.nextCursor();
    if (cursor) {
      await this.loadPersonasWithDirection(
        cursor,
        'NEXT',
        this.currentPage() + 1
      );
    }
  }

  /**
   * Carga la página anterior usando previousCursor del backend
   */
  async loadPreviousPage(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) return;

    const prevCursor = this.previousCursor();
    const newPage = Math.max(1, this.currentPage() - 1);

    // Si vamos a página 1, ir a primera página limpia
    if (newPage === 1) {
      await this.loadPersonas(undefined, 1);
    } else if (prevCursor) {
      await this.loadPersonasWithDirection(prevCursor, 'PREVIOUS', newPage);
    } else {
      // Fallback: ir a primera página
      await this.irPrimeraPagina();
    }
  }

  /**
  * Carga personas con dirección específica (NEXT o PREVIOUS)
   * @param pagina página destino; currentPage cambia solo tras la respuesta
   */
  async loadPersonasWithDirection(
    cursor: string,
    direction: 'NEXT' | 'PREVIOUS',
    pagina: number
  ): Promise<void> {
    this.loading.set(true);
    try {
      const response =
        await this.personaRepository.getPageWithFiltersAndDirection(
          cursor,
          this.pageSize(),
          this.filtro,
          direction
        );
      this.aplicarRespuesta(response, pagina);
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar las personas'
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Cambia el tamaño de página
   */
  onPageSizeChange(): void {
    if (this.loading()) {
      // Se descarta el cambio: el selector vuelve al tamaño vigente
      this.selectedPageSize = this.pageSize();
      return;
    }
    this.pageSize.set(this.selectedPageSize);
    this.cursorHistory = [];
    this.loadPersonas(undefined, 1);
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  /**
   * Abre el dialog para crear nueva persona
   */
  openNew(): void {
    this.personaForm = createEmptyPersona();
    this.editMode.set(false);
    this.submitted.set(false);
    this.activeTabIndex.set(0);
    this.dialogVisible = true;
  }

  /**
   * Abre el dialog para editar persona existente
   * IMPORTANTE: Recarga datos frescos del servidor para evitar sobrescribir cambios de otros usuarios
   */
  async editPersona(persona: Persona): Promise<void> {
    if (!persona.cdpersona) {
      return;
    }

    if (this.openingPersonaDialog()) {
      return;
    }

    this.openingPersonaDialog.set(true);

    try {
      // Recargar datos frescos del servidor para evitar conflictos de concurrencia
      const personaFresca = await this.personaRepository.getById(
        persona.cdpersona
      );
      this.personaForm = { ...personaFresca };

      // Convertir fecha de nacimiento de string a Date para el DatePicker
      if (personaFresca.fenacimiento) {
        this.personaForm.fenacimiento = new Date(personaFresca.fenacimiento);
      }

      this.editMode.set(true);
      this.submitted.set(false);
      this.activeTabIndex.set(0);
      this.dialogVisible = true;
      this.cdr.detectChanges();
      // El componente PersonaDialogComponent cargará los nombres de municipio y actividad
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar los datos de la persona'
      });
    } finally {
      this.openingPersonaDialog.set(false);
    }
  }

  /**
   * Oculta el dialog
   */
  hideDialog(): void {
    this.dialogVisible = false;
    this.submitted.set(false);
    this.activeTabIndex.set(0);
  }

  /**
  * Verifica si un campo es inválido (vacío y requerido)
   * @param value - Valor del campo
  * @param isRequired - Si es true, valida que no está vacío (default: true)
   */
  isFieldInvalid(
    value: string | null | undefined,
    isRequired: boolean = true
  ): boolean {
    if (!isRequired) return false;
    return this.submitted() && (!value || value.trim() === '');
  }

  /**
  * Valida si el formulario es válido
  * Aplica validaciones condicionales según tipo de identificación:
   * - NIT (persona jurídica): requiere nombre_comercial, nombre_legal, digito_verificacion
   * - Otros (persona natural): requiere nombre1, apellido1
   */
  isFormValid(): boolean {
    const f = this.personaForm;

    // Validaciones comunes para todos
    const commonValid = !!(
      f.cdtipo_identificacion && f.nmidentificacion?.trim()
    );

    if (this.isPersonaJuridica()) {
      // Validaciones para persona jurídica (NIT)
      return (
        commonValid &&
        !!(
          f.cddigito_verificacion?.trim() &&
          f.dsnombre_comercial?.trim() &&
          f.dsnombre_legal?.trim()
        )
      );
    } else {
      // Validaciones para persona natural
      return commonValid && !!(f.dsnombre1?.trim() && f.dsapellido1?.trim());
    }
  }

  /**
   * Guarda la persona (crear o actualizar)
   */
  async savePersona(): Promise<void> {
    this.submitted.set(true);

    // Validación de campos obligatorios
    if (!this.isFormValid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Complete los campos obligatorios'
      });
      return;
    }

    this.saving.set(true);
    try {
      // Preparar datos para envío (convertir fecha a formato ISO)
      const personaToSave = this.preparePersonaForSave();

      if (this.editMode() && this.personaForm.cdpersona) {
        await this.personaRepository.update(
          this.personaForm.cdpersona,
          personaToSave
        );
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Persona actualizada correctamente'
        });
      } else {
        await this.personaRepository.create(personaToSave);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Persona creada correctamente'
        });
      }
      this.hideDialog();
      this.loadPersonas();
    } catch (error: any) {
      // Extraer mensaje amigable del error de validación
      const userMessage = this.extractUserFriendlyErrorMessage(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: userMessage
      });
    } finally {
      this.saving.set(false);
    }
  }

  /**
   * Prepara la persona para envío al backend
   * Convierte la fecha de nacimiento a formato yyyy-MM-dd
   * Limpia campos no aplicables según tipo de persona
   */
  private preparePersonaForSave(): Persona {
    const persona = { ...this.personaForm };

    // Convertir fecha de Date a string yyyy-MM-dd
    if (persona.fenacimiento instanceof Date) {
      const date = persona.fenacimiento;
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      persona.fenacimiento = `${year}-${month}-${day}`;
    }

    // Limpiar y ajustar campos según tipo de persona (patrón del legacy)
    if (this.isPersonaJuridica()) {
      // Para persona jurídica (NIT):
      // - dsnombre1 = nombre comercial/razón social
      // - dsnombre_comercial = mismo valor
      // - dsnombre2, dsapellido1, dsapellido2 = null (el legacy los guarda así)
      persona.dsnombre1 = persona.dsnombre_comercial || '';
      persona.dsnombre2 = undefined;
      persona.dsapellido1 = undefined;
      persona.dsapellido2 = undefined;
      persona.cdsexo = undefined;
      persona.fenacimiento = undefined;
    } else {
      // Para persona natural (CC, etc.):
      // - Nombres y apellidos en sus campos respectivos
      // - dsnombre_comercial = nombre completo concatenado (patrón legacy)
      const nombreCompleto = [
        persona.dsnombre1,
        persona.dsnombre2,
        persona.dsapellido1,
        persona.dsapellido2
      ]
        .filter(Boolean)
        .join(' ');
      persona.dsnombre_comercial = nombreCompleto || undefined;
      persona.dsnombre_legal = undefined;
      persona.nmtrabajadores = undefined;
      persona.cdtipo_empresa = undefined;
      persona.cdtamanio_empresa = undefined;
      persona.cddigito_verificacion = undefined;
    }

    return persona;
  }

  // =============================================
  // HANDLERS DEL DIÁLOGO PERSONA
  // =============================================

  /**
   * Handler cuando el diálogo emite onSave
   * La persona ya viene validada y preparada desde el componente hijo
   */
  async onDialogSave(persona: Persona): Promise<void> {
    this.saving.set(true);
    try {
      if (this.editMode() && persona.cdpersona) {
        await this.personaRepository.update(persona.cdpersona, persona);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Persona actualizada correctamente'
        });
      } else {
        await this.personaRepository.create(persona);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Persona creada correctamente'
        });
      }
      // Notificar al diálogo que el guardado fue exitoso
      this.personaDialog?.finalizarGuardado(true);
      this.dialogVisible = false;
      this.loadPersonas();
    } catch (error: any) {
      const userMessage = this.extractUserFriendlyErrorMessage(error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: userMessage
      });

      // Notificar al diálogo que hubo error
      this.personaDialog?.finalizarGuardado(false);
    } finally {
      this.saving.set(false);
    }
  }

  /**
   * Handler cuando el diálogo se cancela
   */
  onDialogCancel(): void {
    this.dialogVisible = false;
    this.submitted.set(false);
  }

  /**
  * Muestra confirmación para eliminar
   */
  confirmDelete(persona: Persona): void {
    this.confirmationService.confirm({
      message: `¿Está seguro de eliminar a ${getNombreCompleto(persona)}?`,
      header: 'Confirmar Eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => this.deletePersona(persona)
    });
  }

  /**
   * Elimina (inactiva) una persona
   */
  async deletePersona(persona: Persona): Promise<void> {
    if (!persona.cdpersona) return;
    try {
      await this.personaRepository.delete(persona.cdpersona);
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Persona eliminada correctamente'
      });
      this.loadPersonas();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo eliminar la persona'
      });
    }
  }

  /**
   * Obtiene el label del sexo
   */
  getSexoLabel(value: string | undefined): string {
    if (!value) return '-';
    const tipo = this.tiposSexo.find((t) => t.codigo === value);
    return tipo?.descripcion || value;
  }

  /**
  * Obtiene la descripción del tipo de identificación
   * Equivalente a llenarListBox + getDefaultValueIndex del legacy
   */
  getTipoIdLabel(codigo: string | undefined): string {
    if (!codigo) return '-';
    const tipo = this.tiposIdentificacion().find((t) => t.codigo === codigo);
    return tipo?.descripcion?.toUpperCase() || codigo;
  }

  /**
   * Extrae mensaje de error amigable para el usuario
   *
   * El backend ahora envía:
   * - detail: mensaje del primer error (limpio, sin prefijo de campo)
  * - validationErrors: [{field, message}, ...] para múltiples errores
   */
  private extractUserFriendlyErrorMessage(error: any): string {
    // El backend ya envía el mensaje limpio en detail
    const detail = error?.error?.detail;
    if (detail && typeof detail === 'string') {
      return detail;
    }

    // Fallback: si hay validationErrors, usar el primero
    const validationErrors = error?.error?.validationErrors;
    if (
      validationErrors &&
      Array.isArray(validationErrors) &&
      validationErrors.length > 0
    ) {
      return validationErrors[0].message || 'Error de validación';
    }

    return 'No se pudo guardar la persona';
  }
}
